import { motion } from "framer-motion";

/**
 * Ma Petite Yole — Fanion badge (rank pennant).
 * Levels: 1 Mousse · 2 Bwa Dréssé · 3 Patron · 4 MAPIPI.
 *
 * Props:
 *  - level: 1|2|3|4
 *  - size: "xs" | "sm" | "md" | "lg" | "xl"
 *  - showLabel: bool  — render the label next to the flag
 *  - onDark: bool     — hint that the container is dark (adjusts label color)
 *  - animated: bool   — subtle sway
 */
export const FANION_LABELS = {
  1: "Mousse",
  2: "Bwa Dréssé",
  3: "Patron",
  4: "MAPIPI",
};

const FILES = {
  1: "/branding/fanions/fanion-1-mousse.svg",
  2: "/branding/fanions/fanion-2-boisdresse.svg",
  3: "/branding/fanions/fanion-3-patron.svg",
  4: "/branding/fanions/fanion-4-metyole.svg",
};

const SIZE_PX = { xs: 14, sm: 20, md: 28, lg: 56, xl: 120 };

export function FanionBadge({
  level = 1,
  size = "sm",
  showLabel = false,
  onDark = false,
  animated = false,
  className = "",
}) {
  const lvl = Math.max(1, Math.min(4, Number(level) || 1));
  const px = SIZE_PX[size] || SIZE_PX.sm;
  const src = FILES[lvl];
  const label = FANION_LABELS[lvl];

  const img = (
    <img
      src={src}
      alt={`Fanion ${label}`}
      width={px}
      height={Math.round(px * (160 / 140))}
      style={{ height: "auto" }}
      className="inline-block flex-shrink-0"
      data-testid={`fanion-${lvl}`}
    />
  );

  const wrapped = animated ? (
    <motion.span
      initial={{ rotate: -3 }}
      animate={{ rotate: [2, -2, 2] }}
      transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut" }}
      style={{ display: "inline-flex", transformOrigin: "top left" }}
    >
      {img}
    </motion.span>
  ) : (
    img
  );

  if (!showLabel) return <span className={className}>{wrapped}</span>;

  const labelClass =
    size === "xl" || size === "lg"
      ? "brand-wordmark text-lg"
      : "text-[10px] uppercase tracking-widest font-bold";

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      {wrapped}
      <span
        className={`${labelClass} ${onDark ? "text-[color:var(--sunshine)]" : "text-[color:var(--deep)]"}`}
      >
        {label}
      </span>
    </span>
  );
}
